import type { RigaPreventivoLetta } from '../../api/preventivi';

/*  LO STESSO ARTICOLO A DUE PREZZI DIVERSI, DENTRO LO STESSO PREVENTIVO.

    Chiesto in riunione il 14/09/2026. Succede senza che nessuno lo voglia: la stessa
    valvola entra nel capitolo «centrale termica» e poi nel capitolo «sottostazione», le
    due righe nascono in momenti diversi, e a una delle due qualcuno cambia il ricarico o
    forza il costo. Il preventivo esce con la stessa voce a 84 € e a 91 €, e a trovarlo è
    il cliente.

    SI SEGNALA, NON SI IMPEDISCE. Due prezzi diversi per lo stesso codice sono a volte la
    cosa giusta — quantità diverse, una posa più scomoda, una riga tenuta ferma da un
    accordo col fornitore — e un blocco costringerebbe ad aggirarlo cambiando il codice,
    che è peggio del problema. Chi preventiva deve poterlo vedere e decidere.

    IL CONFRONTO È SUL PREZZO UNITARIO AL CLIENTE, non sul costo. È quello che finisce
    stampato e che qualcuno può mettere in fila: due costi diversi che il ricarico riporta
    allo stesso prezzo non sono un problema di chi legge l'offerta. Il costo viene
    riportato accanto perché è quasi sempre lì che sta la causa.

    I centesimi: si confronta arrotondato al centesimo, perché è la cifra che si stampa.
    Sotto, la differenza non esiste per nessuno e segnalarla vorrebbe dire un avviso
    permanente che si impara a ignorare.  */
export interface PrezzoDoppio {
  /** Sigla e codice del produttore, come si leggono sulla riga. */
  codice: string;
  /** La descrizione della prima riga: serve a riconoscere di cosa si parla. */
  descrizione: string;
  /** I prezzi unitari diversi trovati, dal più basso al più alto. */
  prezzi: number[];
  /** Le righe coinvolte, per accendere la marcatura dove sono. */
  righeId: string[];
}

/** Il prezzo unitario al cliente, al centesimo. Null quando non se ne può calcolare uno. */
function prezzoUnitario(riga: RigaPreventivoLetta): number | null {
  if (riga.quantita <= 0) return null;
  return Math.round((riga.prezzo / riga.quantita) * 100) / 100;
}

/*  Le righe che portano un articolo del catalogo, raggruppate per codice. Le voci a mano
    restano fuori: «posa in opera» scritto in due capitoli con due importi diversi è la
    normalità, non un difetto, perché sono due pose diverse.  */
export function prezziDoppi(righe: readonly RigaPreventivoLetta[]): PrezzoDoppio[] {
  const perCodice = new Map<string, { descrizione: string; prezzi: Map<number, string[]> }>();

  for (const riga of righe) {
    if (riga.separatore) continue;
    if (riga.livello !== 3) continue;
    if (!riga.siglaProduttore || !riga.codiceArticoloProduttore) continue;

    const prezzo = prezzoUnitario(riga);
    if (prezzo === null) continue;

    const codice = `${riga.siglaProduttore} ${riga.codiceArticoloProduttore}`;
    const gruppo = perCodice.get(codice) ?? { descrizione: riga.descrizione, prezzi: new Map() };
    gruppo.prezzi.set(prezzo, [...(gruppo.prezzi.get(prezzo) ?? []), riga.id]);
    perCodice.set(codice, gruppo);
  }

  const doppi: PrezzoDoppio[] = [];

  for (const [codice, gruppo] of perCodice) {
    if (gruppo.prezzi.size < 2) continue;

    doppi.push({
      codice,
      descrizione: gruppo.descrizione,
      prezzi: [...gruppo.prezzi.keys()].sort((a, b) => a - b),
      righeId: [...gruppo.prezzi.values()].flat(),
    });
  }

  /*  Il caso più grave per primo: la differenza in valore assoluto fra il minimo e il
      massimo. Con dieci segnalazioni, le prime due sono quelle da guardare.  */
  return doppi.sort(
    (a, b) =>
      b.prezzi[b.prezzi.length - 1]! - b.prezzi[0]! - (a.prezzi[a.prezzi.length - 1]! - a.prezzi[0]!),
  );
}

/** Gli id di tutte le righe coinvolte, per marcarle nella tabella. */
export function righeConPrezzoDoppio(righe: readonly RigaPreventivoLetta[]): Set<string> {
  return new Set(prezziDoppi(righe).flatMap((d) => d.righeId));
}
